import { CartAddEvent } from '@theme/events';

/**
 * "Birlikte Daha İyi" listesini yöneten web bileşeni.
 *
 * Onay kutusu ile seçilen ürünleri, listenin altındaki tek "Sepete Ekle"
 * butonuna basıldığında toplu olarak (/cart/add.js üzerinden) sepete ekler.
 * Her ürünün varsayılan (ilk uygun) varyantı kullanılır.
 *
 * Ekleme başarılı olduğunda temanın standart CartAddEvent'ini tetikler,
 * böylece sepet çekmecesi ve sepet ikonu tema genelindeki mekanizmayla
 * otomatik olarak güncellenir.
 */
class BetterTogetherList extends HTMLElement {
  connectedCallback() {
    this.checkboxes = Array.from(this.querySelectorAll('[data-better-together-toggle]'));
    this.bulkAddButton = this.querySelector('[data-better-together-bulk-add]');
    this.liveRegion = this.querySelector('[data-better-together-live-region]');
    this.defaultButtonText = this.bulkAddButton?.textContent.trim() ?? '';

    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => this.updateButtonState());
    });

    this.bulkAddButton?.addEventListener('click', () => this.addSelectedToCart());

    this.updateButtonState();
  }

  /**
   * @returns {HTMLElement[]} Şu anda işaretli satırların (better-together-item) listesi.
   */
  getSelectedItems() {
    return this.checkboxes
      .filter((checkbox) => /** @type {HTMLInputElement} */ (checkbox).checked)
      .map((checkbox) => checkbox.closest('better-together-item'))
      .filter(/** @returns {item is HTMLElement} */ (item) => item != null);
  }

  updateButtonState() {
    if (!this.bulkAddButton) return;

    const count = this.getSelectedItems().length;
    this.bulkAddButton.disabled = count === 0;
    this.bulkAddButton.textContent = count > 0 ? `${count} Ürünü Sepete Ekle` : this.defaultButtonText;
  }

  async addSelectedToCart() {
    if (!this.bulkAddButton) return;

    const selectedItems = this.getSelectedItems();
    if (selectedItems.length === 0) return;

    const items = selectedItems.map((item) => ({
      id: Number(item.dataset.variantId),
      quantity: 1,
    }));

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sections = Array.from(cartItemsComponents)
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter((id) => Boolean(id));

    const currentButtonText = this.bulkAddButton.textContent;
    this.bulkAddButton.disabled = true;

    try {
      const response = await fetch(window.Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items, sections }),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.message || result.description || 'Sepete eklenirken bir hata oluştu.');
      }

      document.dispatchEvent(
        new CartAddEvent({}, this.id || 'better-together', {
          source: 'better-together',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: result.sections,
        })
      );

      if (this.liveRegion) {
        this.liveRegion.textContent = 'Seçilen ürünler sepete eklendi.';
      }

      selectedItems.forEach((item) => {
        const checkbox = /** @type {HTMLInputElement | null} */ (item.querySelector('[data-better-together-toggle]'));
        if (checkbox) checkbox.checked = false;
      });
    } catch (error) {
      console.error(error);
      if (this.liveRegion) {
        this.liveRegion.textContent = 'Sepete eklenirken bir hata oluştu, lütfen tekrar deneyin.';
      }
      this.bulkAddButton.textContent = currentButtonText;
    } finally {
      this.updateButtonState();
    }
  }
}

if (!customElements.get('better-together-list')) {
  customElements.define('better-together-list', BetterTogetherList);
}
